import type { GridProps } from '@mui/material'
import { Grid, Paper } from '@mui/material'
import type { Chord, ChordVariant } from '@tunasong/music-lib'
import { ChordLib, getAllChords, GuitarLib, isChordVariant } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { GuitarChord } from '../guitar/index.js'

export interface ChordLibraryProps extends Omit<GridProps, 'onChange'> {
  selected?: Chord
  scale?: Scale | null
  // If specified, only show these chords. @default all chords
  chords?: ChordVariant[]

  onChange(chord: Chord): void
}

export const ChordLibrary: FC<ChordLibraryProps> = props => {
  const { selected, chords = getAllChords(), scale, onChange, ...gridProps } = props

  const handleFocus = (focusChord: ChordVariant) => () => {
    if (!ChordLib.equals(focusChord, selected)) {
      onChange(focusChord)
    }
  }

  const handleSelect = (c: Chord) => () => {
    onChange(c)
  }
  const handleKeyDown = (ch: Chord) => (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      ev.preventDefault()
      onChange(ch)
    }
  }

  /** Focus on the active chord   */
  const [activeRef, setActiveRef] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    activeRef?.focus()
  }, [activeRef])

  const showChords = chords.filter(Boolean)

  return (
    <Grid container={true} spacing={2} {...gridProps}>
      {showChords.map(ch => {
        const fingering = GuitarLib.getFingering(ch)
        const matchChord = isChordVariant(selected) ? selected : undefined

        return ch && fingering ? (
          <Grid key={ChordLib.getName(ch)}>
            <Paper
              sx={{
                padding: 1,
                paddingBottom: 0,
                ...(ChordLib.equals(ch, matchChord) && {
                  backgroundColor: 'action.hover',
                  borderColor: 'primary.main',
                  borderWidth: 2,
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }),
              }}
              ref={ChordLib.equals(ch, matchChord) ? setActiveRef : null}
              variant="outlined"
              tabIndex={0}
              onClick={handleSelect(ch)}
              onFocus={handleFocus(ch)}
              onKeyDown={handleKeyDown(ch)}
            >
              <GuitarChord chord={ch} scale={scale} />
            </Paper>
          </Grid>
        ) : null
      })}
    </Grid>
  )
}

export default ChordLibrary
